import { useState, useEffect, useCallback } from 'react';
import { Card, Button, Select, Typography, message, Spin, Tag, Tooltip, Space } from 'antd';
import { SaveOutlined, ReloadOutlined } from '@ant-design/icons';
import PageHeader from '@/components/ui/PageHeader';
import { adminApi } from '@/api/adminApi';
import { UserRole, RaciType, RaciMatrixEntry } from '@/types/common';
import { getPhaseLabel } from '@/utils/phaseHelpers';
import {
  getRaciTypeBgColor,
  getRaciTypeColor,
  getRaciTypeLabel,
  RaciTypeCode,
} from '@/utils/raciHelpers';
import type { RfoPhase } from '@/types/workflow';

const { Text } = Typography;

const ROLES: UserRole[] = [
  'CEO',
  'BD_MANAGER',
  'ENTERPRISE_ARCHITECT',
  'CLOUD_ARCHITECT',
  'OPPORTUNITY_FINDER',
  'REQUIREMENT_RESOURCING_MGR',
  'TECHNICAL_WRITER',
  'QC_LEAD',
];

const ROLE_LABELS: Record<UserRole, string> = {
  CEO: 'CEO',
  BD_MANAGER: 'BD Manager',
  ENTERPRISE_ARCHITECT: 'Enterprise Architect',
  CLOUD_ARCHITECT: 'Cloud Architect',
  OPPORTUNITY_FINDER: 'Opportunity Finder',
  REQUIREMENT_RESOURCING_MGR: 'Resourcing Manager',
  TECHNICAL_WRITER: 'Technical Writer',
  QC_LEAD: 'QC Lead',
  CONTRIBUTOR: 'Contributor',
  ADMIN: 'Admin',
};

const PHASES: RfoPhase[] = [
  'PHASE_A',
  'PHASE_B',
  'PHASE_C',
  'PHASE_D',
  'PHASE_E',
  'PHASE_F',
];

type CellKey = string;
type MatrixState = Record<CellKey, RaciType | null>;

const buildCellKey = (role: UserRole, phase: string): CellKey => `${role}_${phase}`;

const RaciConfigPage = () => {
  const [matrix, setMatrix] = useState<MatrixState>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadMatrix = useCallback(async () => {
    setLoading(true);
    try {
      const entries = await adminApi.getRaciMatrix();
      const state: MatrixState = {};
      entries.forEach((entry) => {
        const key = buildCellKey(entry.role, entry.phase);
        state[key] = entry.raciType;
      });
      setMatrix(state);
    } catch (err) {
      console.error('Failed to load RACI matrix:', err);
      message.error('Failed to load RACI matrix');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMatrix();
  }, [loadMatrix]);

  const handleCellChange = (role: UserRole, phase: string, value: RaciType | null) => {
    const key = buildCellKey(role, phase);
    setMatrix((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const entries: RaciMatrixEntry[] = [];
      for (const role of ROLES) {
        for (const phase of PHASES) {
          const key = buildCellKey(role, phase);
          const value = matrix[key];
          if (value) {
            entries.push({
              role,
              phase,
              raciType: value,
            });
          }
        }
      }
      await adminApi.updateRaciMatrix(entries);
      message.success('RACI matrix saved successfully');
    } catch (err) {
      console.error('Failed to save RACI matrix:', err);
      message.error('Failed to save RACI matrix');
    } finally {
      setSaving(false);
    }
  };

  const raciOptions: RaciTypeCode[] = ['R', 'A', 'C', 'I'];

  return (
    <div>
      <PageHeader
        title="RACI Configuration"
        subtitle={`${ROLES.length} roles · ${PHASES.length} phases`}
        actions={
          <>
            <Button icon={<ReloadOutlined />} onClick={loadMatrix} disabled={loading}>
              Reload
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSave}
              loading={saving}
            >
              Save Changes
            </Button>
          </>
        }
      />

      {/* Legend */}
      <Card size="small" className="ui-animate-in" style={{ marginBottom: 16 }}>
        <Space size="large" wrap>
          <Text strong>Legend:</Text>
          {raciOptions.map((type) => (
            <Tag
              key={type}
              color={getRaciTypeColor(type)}
              style={{ fontWeight: 'bold' }}
            >
              {type} - {getRaciTypeLabel(type)}
            </Tag>
          ))}
          <Tag>None - No assignment</Tag>
        </Space>
      </Card>

      <Card className="ui-animate-in">
        <Spin spinning={loading}>
          <div style={{ overflowX: 'auto' }}>
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                minWidth: 900,
              }}
            >
              <thead>
                <tr>
                  <th
                    style={{
                      padding: '12px 16px',
                      borderBottom: '2px solid var(--color-border-light)',
                      textAlign: 'left',
                      backgroundColor: 'var(--color-bg-muted)',
                      fontWeight: 600,
                      minWidth: 180,
                    }}
                  >
                    Role
                  </th>
                  {PHASES.map((phase) => (
                    <th
                      key={phase}
                      style={{
                        padding: '12px 8px',
                        borderBottom: '2px solid var(--color-border-light)',
                        textAlign: 'center',
                        backgroundColor: 'var(--color-bg-muted)',
                        fontWeight: 600,
                        minWidth: 110,
                        fontSize: 12,
                      }}
                    >
                      <Tooltip title={getPhaseLabel(phase)}>
                        <div>{phase.replace('PHASE_', 'Gate ')}</div>
                        <div
                          style={{
                            fontSize: 11,
                            fontWeight: 400,
                            color: 'var(--color-text-tertiary)',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: 110,
                          }}
                        >
                          {getPhaseLabel(phase)}
                        </div>
                      </Tooltip>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ROLES.map((role) => (
                  <tr key={role}>
                    <td
                      style={{
                        padding: '10px 16px',
                        borderBottom: '1px solid var(--color-border-light)',
                        fontWeight: 500,
                      }}
                    >
                      {ROLE_LABELS[role]}
                    </td>
                    {PHASES.map((phase) => {
                      const key = buildCellKey(role, phase);
                      const value = matrix[key] || null;
                      const bgColor = value
                        ? getRaciTypeBgColor(value as RaciTypeCode)
                        : 'var(--color-bg-base)';

                      return (
                        <td
                          key={phase}
                          style={{
                            padding: '6px 8px',
                            borderBottom: '1px solid var(--color-border-light)',
                            textAlign: 'center',
                            backgroundColor: bgColor,
                            transition: 'background-color 0.2s',
                          }}
                        >
                          <Select
                            value={value}
                            onChange={(val) =>
                              handleCellChange(role, phase, val || null)
                            }
                            size="small"
                            style={{ width: 80 }}
                            allowClear
                            placeholder="-"
                          >
                            {raciOptions.map((opt) => (
                              <Select.Option key={opt} value={opt}>
                                <span
                                  style={{
                                    color: getRaciTypeColor(opt),
                                    fontWeight: 'bold',
                                  }}
                                >
                                  {opt}
                                </span>
                              </Select.Option>
                            ))}
                          </Select>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Spin>
      </Card>
    </div>
  );
};

export default RaciConfigPage;
